import React, { useEffect, useState } from "react";
import Button from "@amzn/awsui-components-core/button";
import FormField from "@amzn/awsui-components-core/form-field";
import Input from "@amzn/awsui-components-core/input";

// Same rule as the backend (ASIN_PATTERN in find_transaction/filtering.py).
const ASIN_PATTERN = /^[A-Z0-9]{10}$/;

export const normalizeAsin = (value: string) => value.trim().toUpperCase();

export const isValidAsin = (value: string) =>
  ASIN_PATTERN.test(normalizeAsin(value));

interface FeaturesAsinFooterProps {
  appliedAsin: string;
  filterText: string;
  onApply: (asin: string) => void;
  onRemove: () => void;
}

/**
 * ASIN entry pinned to the bottom of the Features dropdown.
 */
export function FeaturesAsinFooter({
  appliedAsin,
  filterText,
  onApply,
  onRemove,
}: FeaturesAsinFooterProps) {
  const [draft, setDraft] = useState(appliedAsin);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    setDraft(appliedAsin);
    setShowError(false);
  }, [appliedAsin]);

  const apply = (value: string) => {
    const asin = normalizeAsin(value);
    if (!isValidAsin(asin)) {
      setShowError(true);
      return;
    }
    setShowError(false);
    setDraft(asin);
    onApply(asin);
  };

  const suggestion = normalizeAsin(filterText);
  const showSuggestion = isValidAsin(suggestion) && suggestion !== appliedAsin;

  return (
    // The dropdown prevents default on mousedown to keep focus in its filter
    // box, which would stop this input from ever taking focus on click.
    <div
      className="features-asin-footer"
      data-testid="features-asin-footer"
      onMouseDown={(event) => event.stopPropagation()}
    >
      {showSuggestion && (
        <div className="features-asin-suggestion">
          <Button variant="inline-link" onClick={() => apply(suggestion)}>
            Use {suggestion} as ASIN
          </Button>
        </div>
      )}
      <FormField
        label="ASIN"
        constraintText={showError ? undefined : "10 letters or numbers"}
        errorText={showError ? "ASIN must be 10 letters or numbers" : undefined}
        stretch
      >
        <div className="features-asin-controls">
          <div className="features-asin-input">
            <Input
              value={draft}
              placeholder="e.g. B07WGJTX8V"
              ariaLabel="ASIN"
              onChange={({ detail }) => {
                setDraft(detail.value.toUpperCase());
                setShowError(false);
              }}
              onKeyDown={({ detail }) => {
                if (detail.key === "Enter") {
                  apply(draft);
                }
              }}
              invalid={showError}
              disableBrowserAutocorrect
              spellcheck={false}
            />
          </div>
          <Button onClick={() => apply(draft)}>Apply</Button>
          {appliedAsin && (
            <Button variant="link" onClick={onRemove}>
              Remove
            </Button>
          )}
        </div>
      </FormField>
    </div>
  );
}
